// The five Operators' identity + the chat view-message shape — the live home
// for the few product constants/types the Brief 4 surfaces legitimately reuse,
// relocated here in the P7 cutover off the retired `fixtures/` mock module.
//
// `OPERATORS` is the single source of each Operator's name/role/accent token
// (Supervisor = Guard; Engineering / Design / Product = the three domain
// Arbiters; Signal = Signal); it is a fixed product constant, not mock data.
// The display names are the current canon over the stable internal ids
// supervisor/engineering/design/product/signal (the keys here, the
// wire/rule/SSE values). The `Message` view shape (+ its `MessageFrom` lane
// tag and inline-choice rows) is the vocabulary the shared `MessageList`
// renders, reused by the share landing's chat mapping.
//
// `OperatorId` is the canonical five-key union from the shared contract layer
// (`../shared`), imported here so there is exactly one such type across the
// app.

import type { OperatorId } from "@waytide/core";

export type { OperatorId };

export type OperatorRole = "Guard" | "Arbiter" | "Signal";

export interface Operator {
  id: OperatorId;
  name: string;
  role: OperatorRole;
  /** Tailwind text-color class drawn from the theme tokens. Operators
   * are the only roster rows allowed a name colour, and only when
   * load-bearing. */
  accentClass: string;
}

/** Presence is the single colour signal in the roster. */
export type Presence = "active" | "idle" | "absent";

/** A single line in a thread. The renderer picks layout from `from`:
 *  - "you"    → indents right, no spine
 *  - "operator"→ accent spine + accent name (uses operatorId)
 *  - "person" → plain flush-left
 *  - "agent-in"/"agent-out" → the two-way agent exchange, mono
 *  - "evidence" → mono / faint proof line
 *  - "system" → faint inline note (e.g. auto-pause)
 */
export type MessageFrom =
  | "you"
  | "operator"
  | "person"
  | "agent-in"
  | "agent-out"
  | "evidence"
  | "system";

export interface InlineChoice {
  /** Plain inline label, e.g. "open Trace", "let it pass". */
  label: string;
  /** Mock-only: no handler wiring yet. */
  emphasis?: boolean;
}

export interface Message {
  id: string;
  from: MessageFrom;
  /** Author display name. Omitted for evidence/agent/system lines that
   * carry their own mono prefix. */
  author?: string;
  operatorId?: OperatorId;
  /** Body text. Machine bodies (agent exchange, evidence) render mono. */
  text: string;
  when?: string;
  /** When true, collapse the author header — part of a grouped run. */
  sameAuthor?: boolean;
  /** Inline choices rendered after the line (operator interventions). */
  choices?: InlineChoice[];
}

// The five Operators and their accent tokens (Brief 5): the design source
// defines three distinct watch-* identity colours, none of them the reserved
// signal-attention amber ("needs you" stays exclusive).
// Supervisor = dusk (the guard), Signal = tide (the signal), and the three
// domain Arbiters — Engineering / Design / Product — SHARE the sea hue: its
// token still carries the legacy `watch-arbiter` id until the design-token
// rename lands separately.
//
// This is the single source of each Operator's name/role/accent across the
// surfaces that render them (the share landing's MessageList, the per-session
// rules panel's framing). The keys/ids are the stable wire contract
// (supervisor/engineering/design/product/signal); the displayed `name` is the
// current canon identity.
export const OPERATORS: Record<string, Operator> = {
  supervisor: { id: "supervisor", name: "Supervisor", role: "Guard", accentClass: "text-watch-supervisor" },
  engineering: { id: "engineering", name: "Engineering", role: "Arbiter", accentClass: "text-watch-arbiter" },
  design: { id: "design", name: "Design", role: "Arbiter", accentClass: "text-watch-arbiter" },
  product: { id: "product", name: "Product", role: "Arbiter", accentClass: "text-watch-arbiter" },
  signal: { id: "signal", name: "Signal", role: "Signal", accentClass: "text-watch-signal" },
};

/** Collapse an aside sender kind to its base operator id, so a product-only
 * `signal_aside` message (Signal's brain questions/answers, which the bridge
 * never delivers to the agent) renders as Signal — not as the neutral agent.
 * Brief 5. */
export function baseOperatorKind(senderKind: string): string {
  return senderKind === "signal_aside" ? "signal" : senderKind;
}

/** The Operator for a chat `sender_kind` (after collapsing asides), or null
 * when the sender is not one of the five Operators. */
export function operatorForKind(senderKind: string): Operator | null {
  return OPERATORS[baseOperatorKind(senderKind)] ?? null;
}
